import { useRef, useState } from "react";

interface TransferReceiptUploadProps {
  onFileChange: (file: File | null, previewUrl: string | null) => void;
  required?: boolean;
  error?: string;
}

export default function TransferReceiptUpload({ onFileChange, required = false, error }: TransferReceiptUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const handleFile = (file: File | null) => {
    if (!file) {
      setPreview(null);
      setFileName(null);
      onFileChange(null, null);
      return;
    }

    // Validate type
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/heic", "application/pdf"];
    if (!allowed.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|heic|pdf)$/i)) {
      return;
    }

    // Validate size (max 10MB)
    if (file.size > 10 * 1024 * 1024) return;

    setFileName(file.name);

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = e.target?.result as string;
        setPreview(url);
        onFileChange(file, url);
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      onFileChange(file, null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(e.target.files?.[0] ?? null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0] ?? null);
  };

  const handleRemove = () => {
    setPreview(null);
    setFileName(null);
    onFileChange(null, null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <label className="text-gray-400 text-xs font-medium flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
          <i className="ri-receipt-line text-amber-400"></i>
          إيصال التحويل البنكي
          {required && <span className="text-amber-400">*</span>}
        </label>
        {!required && (
          <span className="text-gray-600 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>اختياري</span>
        )}
      </div>

      {/* Upload Area */}
      {!fileName ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={`relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-8 cursor-pointer transition-all ${
            dragging
              ? "border-amber-400/60 bg-amber-400/8"
              : error
              ? "border-red-500/40 bg-red-500/5 hover:border-red-500/60"
              : "border-white/15 bg-[#1a1a1a] hover:border-amber-400/40 hover:bg-amber-400/5"
          }`}
        >
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl transition-all ${
            dragging ? "bg-amber-400/20" : "bg-white/5"
          }`}>
            <i className={`ri-upload-cloud-2-line text-2xl ${dragging ? "text-amber-400" : "text-gray-500"}`}></i>
          </div>
          <div className="text-center">
            <p className="text-white text-sm font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {dragging ? "أفلت الملف هنا" : "اضغط لرفع الإيصال"}
            </p>
            <p className="text-gray-500 text-xs mt-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
              صورة أو PDF — حجم أقصى 10MB
            </p>
            <p className="text-gray-600 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              JPG, PNG, WEBP, HEIC, PDF
            </p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,.pdf,.heic"
            className="hidden"
            onChange={handleInputChange}
          />
        </div>
      ) : (
        /* Preview / File info */
        <div className="rounded-xl border border-amber-400/25 bg-[#1a1a1a] overflow-hidden">
          {preview ? (
            <div className="relative">
              <img
                src={preview}
                alt="إيصال التحويل"
                className="w-full max-h-48 object-contain bg-[#111]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-image-line text-amber-400 text-sm flex-shrink-0"></i>
                  <span className="text-white text-xs truncate max-w-[180px]" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    {fileName}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleRemove}
                  className="w-7 h-7 flex items-center justify-center bg-red-500/80 hover:bg-red-500 rounded-lg transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-delete-bin-line text-white text-xs"></i>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-400/15 rounded-xl flex-shrink-0">
                <i className="ri-file-pdf-line text-amber-400 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-semibold truncate" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  {fileName}
                </p>
                <p className="text-gray-500 text-[11px] mt-0.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
                  ملف PDF — تم الرفع بنجاح
                </p>
              </div>
              <button
                type="button"
                onClick={handleRemove}
                className="w-7 h-7 flex items-center justify-center text-gray-500 hover:text-red-400 transition-colors cursor-pointer rounded-lg hover:bg-red-400/10 flex-shrink-0"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          )}

          {/* Success badge */}
          <div className="bg-amber-400/8 border-t border-amber-400/15 px-4 py-2 flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-amber-400 text-sm flex-shrink-0"></i>
            <p className="text-amber-400 text-[11px] font-semibold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تم رفع الإيصال — سيتم إرساله مع طلبك
            </p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mr-auto text-gray-500 hover:text-amber-400 text-[11px] transition-colors cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            >
              تغيير
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*,.pdf,.heic"
              className="hidden"
              onChange={handleInputChange}
            />
          </div>
        </div>
      )}

      {error && (
        <p className="text-red-400 text-xs flex items-center gap-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
          <i className="ri-error-warning-line"></i>
          {error}
        </p>
      )}

      <p className="text-gray-600 text-[11px] flex items-start gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
        <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
        سيتم إرسال الإيصال للمتجر عبر واتساب لتأكيد التحويل وتجهيز طلبك
      </p>
    </div>
  );
}
